import axios, {AxiosResponse} from 'axios';
import {
  CreateContactRequestBody,
  FlashyCreateContactResponse,
  GetAnswersBody,
  GetAnswersResponse,
  GetUserByContactIDBody,
  GetUserByContactIDResponse,
  RegisterUserRequest,
  RegisterUserResponse,
  SaveAnswersBody,
  SendChatHistoryByEmailRequest,
  SendChatHistoryByEmailResponse,
  UpdateContactRequest,
  UpdateContactResponse,
  UpdateUserBody,
  UpdateUserResponse
} from '../types';

export class APIService {
  static URL = {
    updateUser: 'api/users/updateUser',
    getUser: 'api/users/getUserByContactID',
    getAnswers: 'api/userAnswers/getAnswers',
    saveAnswers: 'api/userAnswers/saveAnswers',
    sendChatHistoryByEmail: 'api/flashy/sendChatHistoryByEmail',
    updateContact: 'api/flashy/updateContact',
    createFlashyContact: 'api/flashy/createContact',
    createNewUser: 'api/users/createUser',
  };
  private readonly axios = axios.create({
    baseURL: process.env.REACT_APP_API_SERVER,
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
    }
  });

  public async getUserByContactID(contactID: string) {
    const result = await this.axios.post<
      GetUserByContactIDResponse,
      AxiosResponse<GetUserByContactIDResponse>,
      GetUserByContactIDBody
    >(APIService.URL.getUser, {contactID});
    return result.data.user;
  }

  public async getAnswers(contactID: string) {
    const result = await this.axios.post<
      GetAnswersResponse,
      AxiosResponse<GetAnswersResponse>,
      GetAnswersBody
    >(APIService.URL.getAnswers, {contactID});
    return result.data.answers;
  }

  public async saveAnswers(
    contactID: string,
    timeOfYear: string,
    region: string,
    kids: boolean,
    babies: boolean,
    dogs: boolean,
    activities: string[],
    trailsOptions: string,
    mealOptions: string,
  ) {
    await this.axios.post<
      void,
      AxiosResponse<void>,
      SaveAnswersBody
    >(
      APIService.URL.saveAnswers,
      {contactID, timeOfYear, region, kids, babies, dogs, activities, trailsOptions, mealOptions},
    );
  }

  public async sendChatHistoryByEmail(
    contactID: string,
    history: {
      id: string
      timestamp: string
      content: string
      sender: string
    }[]
  ) {
    const result = await this.axios.post<
      SendChatHistoryByEmailResponse,
      AxiosResponse<SendChatHistoryByEmailResponse>,
      SendChatHistoryByEmailRequest
    >(
      APIService.URL.sendChatHistoryByEmail,
      {contactID, history},
    );
    return result.data;
  }

  public async updateContact(
    contactID: string,
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    babies: boolean,
    communityMember: boolean,
    kids12: boolean,
    dogsOwners: string,
    vacationsType: string,
    travelevel: string,
  ) {
    const result = await this.axios.post<
      UpdateContactResponse,
      AxiosResponse<UpdateContactResponse>,
      UpdateContactRequest
    >(
      APIService.URL.updateContact,
      {
        contactID,
        firstName,
        lastName,
        email,
        phone,
        babies,
        communityMember,
        kids12,
        dogsOwners,
        vacationsType,
        travelevel
      },
    );
    return result.data;
  }

  public async updateUser(
    contactID: string,
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    babies: boolean,
    communityMember: boolean,
    kids12: boolean,
    dogsOwners: string,
    vacationsType: string,
    travelevel: string,
  ) {
    const result = await this.axios.post<
      UpdateUserResponse,
      AxiosResponse<UpdateUserResponse>,
      UpdateUserBody
    >(
      APIService.URL.updateUser,
      {
        contactID,
        firstName,
        lastName,
        email,
        phone,
        babies,
        communityMember,
        kids12,
        dogsOwners,
        vacationsType,
        travelevel
      },
    );
    return result.data;
  }

  public async createFlashyContact(
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
  ) {
    const result = await this.axios.post<
      FlashyCreateContactResponse,
      AxiosResponse<FlashyCreateContactResponse>,
      CreateContactRequestBody
    >(
      APIService.URL.createFlashyContact,
      {
        firstName,
        lastName,
        email,
        phone,
      },
    );
    return result.data.user;
  }

  public async createNewUser(
    contactID: string,
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
  ) {
    const result = await this.axios.post<
      RegisterUserResponse,
      AxiosResponse<RegisterUserResponse>,
      RegisterUserRequest
    >(
      APIService.URL.createNewUser,
      {
        contactID,
        firstName,
        lastName,
        email,
        phone,
      },
    );
    return result.data.user;
  }
}